import type { DimensionRow } from "@/lib/short-links/short-links-api";
import { fmtN } from "@/lib/format";

interface Props {
	rows: DimensionRow[];
	/** Traduction des valeurs brutes ; repli sur la valeur elle-même. */
	labels?: Record<string, string>;
	/** Rendu de tête de ligne (drapeau, icône). */
	prefix?: (value: string) => string;
	emptyLabel?: string;
	max?: number;
}

/**
 * Répartition en barres horizontales — même motif que la page de rapport.
 *
 * Les libellés inconnus retombent sur la valeur brute plutôt que d'afficher un
 * vide : un navigateur qui n'est pas dans le dictionnaire reste une information.
 */
export function DimensionBars({ rows, labels, prefix, emptyLabel, max = 8 }: Props) {
	const visible = rows.slice(0, max);
	const total = Math.max(1, ...visible.map((r) => r.clicks));

	if (visible.length === 0) {
		return (
			<div className="muted" style={{ padding: "20px 0", textAlign: "center", fontSize: 12.5 }}>
				{emptyLabel ?? "Aucune donnée sur cette période."}
			</div>
		);
	}

	return (
		<div className="stack" style={{ gap: 10 }}>
			{visible.map((row) => {
				const label = labels?.[row.value] ?? row.value;
				const pct = Math.round((row.clicks / total) * 100);
				return (
					<div key={row.value}>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								fontSize: 12.5,
								marginBottom: 4,
							}}
						>
							<span>
								{prefix ? `${prefix(row.value)} ` : ""}
								{label}
							</span>
							<span className="tn muted">
								{fmtN(row.humans)}
								{row.clicks !== row.humans ? (
									<span style={{ opacity: 0.6 }}> / {fmtN(row.clicks)}</span>
								) : null}
							</span>
						</div>
						<div className="bar thin">
							<i style={{ width: `${pct}%` }} />
						</div>
					</div>
				);
			})}
		</div>
	);
}

/** Drapeau emoji dérivé d'un code ISO à deux lettres. */
export const flagOf = (code: string): string => {
	if (!/^[A-Za-z]{2}$/.test(code)) return "";
	return String.fromCodePoint(
		...[...code.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65),
	);
};

export const COUNTRY_LABEL: Record<string, string> = {
	CM: "Cameroun",
	FR: "France",
	BE: "Belgique",
	CA: "Canada",
	US: "États-Unis",
	GB: "Royaume-Uni",
	CI: "Côte d'Ivoire",
	SN: "Sénégal",
	GA: "Gabon",
	TD: "Tchad",
	CF: "Centrafrique",
	CG: "Congo",
	inconnu: "Non localisé",
};

export const DEVICE_LABEL: Record<string, string> = {
	mobile: "Mobile",
	tablet: "Tablette",
	desktop: "Ordinateur",
	bot: "Robot",
	unknown: "Inconnu",
};

export const INAPP_LABEL: Record<string, string> = {
	whatsapp: "WhatsApp",
	facebook: "Facebook",
	instagram: "Instagram",
	messenger: "Messenger",
	tiktok: "TikTok",
	telegram: "Telegram",
	snapchat: "Snapchat",
	navigateur: "Navigateur",
};

export const REFERRER_LABEL: Record<string, string> = {
	direct: "Direct (SMS, saisie, QR)",
};
